import type { ReactNode } from 'react'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import type { ContractType } from '@/types'
import { CONTRACT_TYPE_DOT } from './constants'
import { chipShadow, restyleLabelClass } from './styles'

export function FormField({
  children,
  gap = 'sm',
  className,
}: {
  children: ReactNode
  gap?: 'sm' | 'md'
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex w-full flex-col items-start',
        gap === 'md' ? 'gap-2' : 'gap-1',
        className
      )}
    >
      {children}
    </div>
  )
}

export function RequiredLabel({
  htmlFor,
  children,
  className,
  compact,
}: {
  htmlFor?: string
  children: ReactNode
  className?: string
  compact?: boolean
}) {
  return (
    <Label
      htmlFor={htmlFor}
      className={cn(
        compact
          ? restyleLabelClass
          : 'inline-flex items-center gap-1 text-sm font-semibold text-[#171a20]',
        className
      )}
    >
      {children}
      <span className='text-[#d63b33]'>*</span>
    </Label>
  )
}

export function QuickDurationChip({
  onSelect,
  children,
  className,
}: {
  onSelect: () => void
  children: ReactNode
  className?: string
}) {
  return (
    <button
      type='button'
      onClick={onSelect}
      className={cn(
        'rounded-xl border border-[#e7e9ef] bg-white px-3 py-2 text-sm font-semibold leading-[1.4] text-[#171a20]',
        'hover:border-[#ff5a1f] hover:bg-[#fff1ea]',
        chipShadow,
        className
      )}
    >
      {children}
    </button>
  )
}

export function FrequencyCard({
  selected,
  onSelect,
  title,
  hint,
  className,
  titleClassName,
  hintClassName,
}: {
  selected: boolean
  onSelect: () => void
  title: string
  hint: string
  className?: string
  titleClassName?: string
  hintClassName?: string
}) {
  return (
    <button
      type='button'
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        'flex w-full flex-col items-center justify-center gap-1 rounded-xl border p-4 text-center',
        chipShadow,
        selected
          ? 'border-[#059669] bg-[#ecfdf5]'
          : 'border-[#e7e9ef] bg-white',
        className
      )}
    >
      <span
        className={cn(
          'text-base font-semibold leading-[1.4] text-[#171a20]',
          titleClassName
        )}
      >
        {title}
      </span>
      <span
        className={cn(
          'text-sm font-normal leading-[1.4] text-[#3d434e]',
          hintClassName
        )}
      >
        {hint}
      </span>
    </button>
  )
}

export function ContractTypeChip({
  type,
  selected,
  onSelect,
  children,
  className,
}: {
  type: ContractType
  selected: boolean
  onSelect: () => void
  children: ReactNode
  className?: string
}) {
  return (
    <button
      type='button'
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        'inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-left text-sm font-semibold leading-[1.4] text-[#171a20]',
        chipShadow,
        selected
          ? 'border-[#059669] bg-[#ecfdf5]'
          : 'border-[#e7e9ef] bg-white',
        className
      )}
    >
      <span
        className='size-2 shrink-0 rounded-full'
        style={{ backgroundColor: CONTRACT_TYPE_DOT[type] }}
      />
      {children}
    </button>
  )
}

export function SelectionChip({
  selected,
  onSelect,
  children,
  className,
}: {
  selected: boolean
  onSelect: () => void
  children: ReactNode
  className?: string
}) {
  return (
    <button
      type='button'
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        'inline-flex items-center rounded-xl border px-4 py-2.5 text-sm font-semibold leading-[1.4]',
        chipShadow,
        selected
          ? 'border-[#059669] bg-[#ecfdf5] text-[#171a20]'
          : 'border-[#e7e9ef] bg-white text-[#171a20]',
        className
      )}
    >
      {children}
    </button>
  )
}

export function CategoryChip({
  selected,
  onSelect,
  className,
  children,
}: {
  selected: boolean
  onSelect: () => void
  className?: string
  children: ReactNode
}) {
  return (
    <button
      type='button'
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        'inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-left text-sm font-semibold leading-[1.4]',
        chipShadow,
        selected
          ? 'border-[#059669] bg-[#ecfdf5] text-[#171a20]'
          : 'border-[#e7e9ef] bg-white text-[#171a20]',
        className
      )}
    >
      {children}
    </button>
  )
}

export function FieldError({ message }: { message?: string }) {
  if (!message) return null
  return <p className='text-xs text-destructive'>{message}</p>
}
